import cn from "@/v2-ui/utils/utils.cn"
import { DotText } from "@/v2-backoffice-client/components/dot"
import InfoGrid, {
  type InfoGridField,
  type InfoGridProps,
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import LayerProdStateDot from "@/v2-backoffice-client/layer/authoring/LayerProdStateDot"
import { useLayerAuthoringStatusMap } from "@/v2-backoffice-client/layer/authoring/layer.authoring.status.query"
import LayerViewUptimeLatency from "@/v2-backoffice-client/layer/view/LayerViewUptimeLatency"
import LayerViewUptimeStatus from "@/v2-backoffice-client/layer/view/LayerViewUptimeStatus"
import LayerVisibilityDot from "@/v2-backoffice-client/layer/visibility/LayerVisibilityDot"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { AGOL_SEARCH_BASE_URL } from "@/v2-backoffice-client/layer/layer.constants"

const FIELDS = createInfoGridFields<LayerFragment>({
  fields: {
    FeatureCode: {
      label: "Feature Code",
      description:
        "The layer's permanent identifier: everything else about it can change, this cannot. The link searches ArcGIS Online for it.",
      accessor: "FeatureCode",
      to: ({ value }) => `${AGOL_SEARCH_BASE_URL}${value}`,
    },
    LayerTypeCode: {
      label: "Type",
      description:
        "How the app draws the layer: feature (vector), map-image or WMS (tiles from a server), group (several sublayers).",
      accessor: "LayerTypeCode",
    },
    Priority: {
      label: "Order",
      description:
        "The layer's position in the app's layer chooser, among all layers. Changed for the whole list in Organize on the Layers page, not here.",
      accessor: "Priority",
    },
    Has3dSupport: {
      label: "3D Support",
      description:
        "Whether the layer can be shown in the app's 3D view as well as on the flat map.",
      accessor: "Has3dSupport",
      renderValue({ value }) {
        return (
          <DotText variant={value ? "success-1" : "destructive"}>
            {value ? "Supported" : "Only 2D"}
          </DotText>
        )
      },
    },
    IsEnabled: {
      label: "Status",
      description:
        'Enabled = live, listed for customers as long as the gates under "Visible to customers" pass. Disabled = hidden from every customer: waiting in the review queue, or rejected.',
      accessor: "IsEnabled",
      renderValue({ value }) {
        return (
          <DotText variant={value ? "success-1" : "destructive"}>
            {value ? "Enabled" : "Disabled"}
          </DotText>
        )
      },
    },
    // "Enabled" is one of five gates; this is the answer to the question
    // the operator actually has. Details (every gate) on hover, the fixing
    // tab on click. Keyed on FeatureCode so "hide empty" never drops it.
    Visibility: {
      label: "Visible to customers",
      description:
        "Whether a customer sees this layer in the app right now, from the five checks the app makes: live, offered in their country, their tenant has access, filed in a category, in the search index. Hover the value for each check; click it to fix one.",
      accessor: "FeatureCode",
      renderValue({ data }) {
        return <LayerVisibilityDot layer={data} />
      },
    },
  },
})

export type LayerViewInfoProps = Omit<
  InfoGridProps<LayerFragment>,
  "data" | "fields"
> & {
  layer: LayerFragment | null | undefined
  // True while the sheet's on-open live probe is in flight (the uptime
  // value waits for it rather than flashing "no monitor").
  isProbing?: boolean
}

function LayerViewInfo(props: LayerViewInfoProps) {
  const { layer, isProbing, className, ...infoGridProps } = props

  // Catalog → production sync state (empty map on prod deployments, hiding
  // the field entirely there).
  const statuses = useLayerAuthoringStatusMap()
  const status = layer ? statuses.get(layer.FeatureCode) : undefined

  // The two uptime items live here rather than in FIELDS because their
  // loading state follows the sheet's probe. Availability (status + 30-day
  // bar + link to the uptime sheet) and the response-time trend are
  // separate items so each stays one glance.
  const uptimeFields: InfoGridField<LayerFragment>[] = [
    {
      id: "Uptime",
      label: "Uptime",
      description:
        "Whether the service behind the layer answers our monitor: current status and the share of checks that passed in the last 24 hours, then the last two weeks with one cell per day (red = checks failed that day).",
      accessor: "FeatureCode",
      renderValue: ({ data }) => (
        <LayerViewUptimeStatus
          featureCode={data.FeatureCode}
          isProbing={isProbing}
        />
      ),
    },
    {
      id: "ResponseTime",
      label: "Response time",
      description:
        "How fast the service answered the monitor over the last 24 hours, hour by hour, and the average. Slow means at least twice its own usual speed.",
      accessor: "FeatureCode",
      renderValue: ({ data }) => (
        <LayerViewUptimeLatency
          featureCode={data.FeatureCode}
          isProbing={isProbing}
        />
      ),
    },
  ]

  const fields = !status
    ? [...FIELDS, ...uptimeFields]
    : [
        ...FIELDS,
        ...uptimeFields,
        {
          id: "ProdState",
          label: "Production",
          description:
            "Layers are edited here, in the dev catalog, and copied to the production catalog with Push prod. This says whether production has this layer's current definition.",
          accessor: () => status.ProdState,
          renderValue: () => <LayerProdStateDot status={status} />,
        },
      ]

  return (
    <InfoGrid
      id="layer-info"
      className={cn("layer-view-info", className)}
      data={layer}
      fields={fields}
      {...infoGridProps}
    />
  )
}

export default LayerViewInfo
